import {
  PlatformAppEvent,
  PlatformAppEventEmitter,
} from '@wix/public-editor-platform-events';
import {
  APPLICATION_CONTEXT_KEY,
  EnvironmentContext,
  ApplicationContext,
} from '@wix/editor-platform-contexts';

import type { EditorPlatformApplication } from '@wix/editor-application';
import {
  createEditorPlatformApplicationContainerError,
  EditorPlatformApplicationContainerErrorCode,
} from './errors';
import { IApplicationSpec } from '../../types';

export const APPLICATION_REGISTRY_KEY = '__APPLICATION_REGISTRY_KEY';

export type IApplicationRegistry = (
  _instance: EditorPlatformApplication,
) => void;

export async function executeApplication(
  events: PlatformAppEventEmitter,
  spec: IApplicationSpec,
  bundle: string,
): Promise<{
  instance: EditorPlatformApplication;
}> {
  // eslint-disable-next-line no-new-func
  const executable = new Function(
    APPLICATION_CONTEXT_KEY,
    APPLICATION_REGISTRY_KEY,
    bundle,
  );

  let instance: EditorPlatformApplication | undefined;

  const applicationRegistryCallback: IApplicationRegistry = (_instance) => {
    if (instance) {
      throw createEditorPlatformApplicationContainerError(
        EditorPlatformApplicationContainerErrorCode.ApplicationExecuteError,
        'Application registry called more than once',
      ).withAppDefinitionId(spec.appDefinitionId);
    }

    if (_instance.type !== spec.type) {
      throw createEditorPlatformApplicationContainerError(
        EditorPlatformApplicationContainerErrorCode.ApplicationExecuteError,
        'Application has different type',
      )
        .withMessage('expected type', spec.type)
        .withMessage('received type', _instance.type);
    }

    instance = _instance;
  };

  try {
    const context = { ...spec };

    executable.call(
      null,
      new ApplicationContext(context, await EnvironmentContext.getInstance()),
      applicationRegistryCallback,
    );
  } catch (e: unknown) {
    throw createEditorPlatformApplicationContainerError(
      EditorPlatformApplicationContainerErrorCode.ApplicationExecuteError,
      (e as Error).message,
    )
      .withAppDefinitionId(spec.appDefinitionId)
      .withParentError(e as Error);
  }

  return new Promise((resolve, reject) => {
    const unsubscribe = events.subscribe((event) => {
      const timeoutId = setTimeout(() => {
        clearTimeout(timeoutId);
        unsubscribe();

        if (!instance) {
          reject(
            createEditorPlatformApplicationContainerError(
              EditorPlatformApplicationContainerErrorCode.ApplicationExecuteError,
              'Application registry was not called, threw by timeout',
            ).withAppDefinitionId(spec.appDefinitionId),
          );
        }
      }, 5000);

      if (
        event.type === PlatformAppEvent.ApplicationInit &&
        event.meta.appDefinitionId === spec.appDefinitionId
      ) {
        clearTimeout(timeoutId);
        unsubscribe();

        if (!instance) {
          reject(
            createEditorPlatformApplicationContainerError(
              EditorPlatformApplicationContainerErrorCode.ApplicationExecuteError,
              'Application registry was not called',
            ).withAppDefinitionId(spec.appDefinitionId),
          );
        }

        resolve({ instance: instance! });
      }
    });
  });
}
